import { createBrowserRouter, Navigate } from 'react-router'
import { AppRoot } from '@/features/analytics/cookie-notice'
import { FullPageLoader, RouteErrorBoundary, NotFoundPage } from './fallbacks'
import { GuestOnly, LifetimeRedirect, RequireAuth, RootRedirect, WorkspaceGate } from './guards'

/** Every page is code-split (brief §81). */
export const router = createBrowserRouter([
  {
    element: <AppRoot />,
    errorElement: <RouteErrorBoundary />,
    // Shown while the first page's code loads (pages are lazy).
    hydrateFallbackElement: <FullPageLoader />,
    children: [
      { index: true, element: <RootRedirect /> },

      {
        element: <GuestOnly />,
        children: [
          {
            path: 'login',
            lazy: async () => ({ Component: (await import('@/features/auth/pages/login-page')).LoginPage }),
          },
          {
            path: 'login/two-factor',
            lazy: async () => ({
              Component: (await import('@/features/auth/pages/two-factor-page')).TwoFactorPage,
            }),
          },
          {
            path: 'register',
            lazy: async () => ({
              Component: (await import('@/features/auth/pages/register-page')).RegisterPage,
            }),
          },
          {
            path: 'forgot-password',
            lazy: async () => ({
              Component: (await import('@/features/auth/pages/forgot-password-page')).ForgotPasswordPage,
            }),
          },
          {
            path: 'reset-password',
            lazy: async () => ({
              Component: (await import('@/features/auth/pages/reset-password-page')).ResetPasswordPage,
            }),
          },
        ],
      },

      { path: 'connect/chrome', lazy: async () => ({ Component: (await import('@/features/browser-extension/connect-page')).ConnectBrowserExtensionPage }) },
      // Reachable signed in or out.
      // An AI assistant connecting: sign in first if needed, then approve.
      {
        path: 'connect',
        lazy: async () => ({ Component: (await import('@/features/ai-tools/connect-pages')).ConnectPage }),
      },
      {
        path: 'verify-email',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/verify-email-page')).VerifyEmailPage,
        }),
      },
      // Changing the sign-in email: links sent by support or the workspace owner.
      {
        path: 'change-email',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/change-email-page')).ChangeEmailPage,
        }),
      },
      {
        path: 'change-email/confirm',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/change-email-page')).ConfirmEmailChangePage,
        }),
      },
      {
        path: 'accept-invitation',
        lazy: async () => ({
          Component: (await import('@/features/auth/pages/accept-invitation-page')).AcceptInvitationPage,
        }),
      },
      // Shared prospect reports (the link's token is the access; no sign-in needed).
      {
        path: 'shared-assessment',
        lazy: async () => ({
          Component: (await import('@/features/reports/shared-assessment-page')).SharedAssessmentPage,
        }),
      },
      {
        path: 'r/:token',
        lazy: async () => ({
          Component: (await import('@/features/prospects/shared-report-page')).SharedReportPage,
        }),
      },
      // A website enquiry's private link (the token in the fragment is the access).
      {
        path: 'support/ticket',
        lazy: async () => ({
          Component: (await import('@/features/support/guest-ticket-page')).GuestTicketPage,
        }),
      },
      {
        path: 'tickets/guest',
        lazy: async () => ({
          Component: (await import('@/features/support/guest-ticket-page')).GuestTicketPage,
        }),
      },
      // Unsubscribe links from emails (signed; no sign-in needed).
      {
        path: 'email/unsubscribe',
        lazy: async () => {
          const { UnsubscribePage } = await import('@/features/notifications/unsubscribe-page')
          return { Component: () => <UnsubscribePage kind="email" /> }
        },
      },
      {
        path: 'reports/unsubscribe',
        lazy: async () => {
          const { UnsubscribePage } = await import('@/features/notifications/unsubscribe-page')
          return { Component: () => <UnsubscribePage kind="report" /> }
        },
      },
      {
        path: 'tips/unsubscribe',
        lazy: async () => {
          const { UnsubscribePage } = await import('@/features/notifications/unsubscribe-page')
          return { Component: () => <UnsubscribePage kind="tips" /> }
        },
      },
      // Lifetime plans' signed email links (no sign-in needed): an email's button, and "Keep updates running".
      {
        path: 'email/open',
        lazy: async () => ({
          Component: (await import('@/features/notifications/updates-link-pages')).EmailLinkPage,
        }),
      },
      {
        path: 'updates/keep-running',
        lazy: async () => ({
          Component: (await import('@/features/notifications/updates-link-pages')).KeepRunningPage,
        }),
      },

      {
        element: <RequireAuth />,
        children: [
          // Support tickets (support.arvaseo.com/tickets in production), with the help center's header.
          {
            path: 'tickets',
            lazy: async () => ({
              Component: (await import('@/features/support/support-shell')).TicketsHome,
            }),
          },
          {
            path: 'tickets/w/:workspaceId',
            element: <WorkspaceGate />,
            children: [
              {
                lazy: async () => ({
                  Component: (await import('@/features/support/support-shell')).SupportShell,
                }),
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (await import('@/features/support/support-page')).SupportPage,
                    }),
                  },
                  {
                    path: ':ticketId',
                    lazy: async () => ({
                      Component: (await import('@/features/support/support-ticket-page')).SupportTicketPage,
                    }),
                  },
                ],
              },
            ],
          },
          {
            path: 'connect/wordpress',
            lazy: async () => ({
              Component: (await import('@/features/wordpress/connect-page')).ConnectWordPressPage,
            }),
          },
          {
            path: 'connect/authorize',
            lazy: async () => ({
              Component: (await import('@/features/ai-tools/connect-pages')).AuthorizeAiToolPage,
            }),
          },
          // arvaseo.com/lifetime/'s buy buttons: Plan & billing's lifetime plans in a workspace you own.
          { path: 'lifetime', element: <LifetimeRedirect /> },
          {
            path: 'account',
            lazy: async () => ({
              Component: (await import('@/features/account/personal-account-page')).PersonalAccountPage,
            }),
          },
          {
            path: 'workspaces/new',
            lazy: async () => ({
              Component: (await import('@/features/workspaces/pages/create-workspace-page'))
                .CreateWorkspacePage,
            }),
          },
          {
            path: 'w/:workspaceId',
            element: <WorkspaceGate />,
            children: [
              // The Agency client portal: client logins, or an owner or admin previewing (?client=).
              {
                path: 'portal',
                lazy: async () => ({
                  Component: (await import('@/features/portal/portal-layout')).PortalLayout,
                }),
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (await import('@/features/portal/portal-home-page')).PortalHomePage,
                    }),
                  },
                  {
                    path: ':projectId',
                    lazy: async () => ({
                      Component: (await import('@/features/portal/portal-project-page')).PortalProjectPage,
                    }),
                  },
                  {
                    path: ':projectId/reports/:reportId',
                    lazy: async () => ({
                      Component: (await import('@/features/portal/portal-report-page')).PortalReportPage,
                    }),
                  },
                  {
                    path: ':projectId/content/:pieceId',
                    lazy: async () => ({
                      Component: (await import('@/features/portal/portal-content-page')).PortalContentPage,
                    }),
                  },
                ],
              },
              {
                lazy: async () => ({ Component: (await import('@/features/shell/app-shell')).AppShell }),
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (await import('@/features/dashboard/dashboard-page')).DashboardPage,
                    }),
                  },
                  {
                    path: 'assistant',
                    lazy: async () => ({
                      Component: (await import('@/features/assistant/assistant-page')).AssistantPage,
                    }),
                  },
                  {
                    path: 'account',
                    lazy: async () => ({
                      Component: (await import('@/features/account/account-page')).AccountPage,
                    }),
                  },
                  {
                    path: 'settings',
                    lazy: async () => ({
                      Component: (await import('@/features/workspaces/pages/settings-layout')).SettingsLayout,
                    }),
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (await import('@/features/workspaces/pages/general-settings-page'))
                            .GeneralSettingsPage,
                        }),
                      },
                      {
                        path: 'members',
                        lazy: async () => ({
                          Component: (await import('@/features/workspaces/pages/members-page')).MembersPage,
                        }),
                      },
                      {
                        path: 'plan',
                        lazy: async () => ({
                          Component: (await import('@/features/workspaces/pages/plan-page')).PlanPage,
                        }),
                      },
                      // Renamed from "Report branding"; old links still work.
                      { path: 'report-branding', element: <Navigate to="../branding" replace /> },
                      {
                        path: 'branding',
                        lazy: async () => ({
                          Component: (await import('@/features/reports/report-branding-page'))
                            .ReportBrandingPage,
                        }),
                      },
                      // AI keys moved to Integrations; old links still work.
                      {
                        path: 'ai',
                        element: <Navigate to="../../integrations#ai" relative="path" replace />,
                      },
                      {
                        path: 'audit-log',
                        lazy: async () => ({
                          Component: (await import('@/features/workspaces/pages/audit-log-page'))
                            .AuditLogPage,
                        }),
                      },
                    ],
                  },
                  {
                    path: 'websites',
                    lazy: async () => ({
                      Component: (await import('@/features/projects/pages/websites-page')).WebsitesPage,
                    }),
                  },
                  {
                    path: 'websites/new',
                    lazy: async () => ({
                      Component: (await import('@/features/projects/pages/new-website-page')).NewWebsitePage,
                    }),
                  },
                  {
                    path: 'clients',
                    lazy: async () => ({
                      Component: (await import('@/features/clients/clients-page')).ClientsPage,
                    }),
                  },
                  {
                    path: 'reports',
                    lazy: async () => ({
                      Component: (await import('@/features/reports/workspace-reports-page'))
                        .WorkspaceReportsPage,
                    }),
                  },
                  {
                    path: 'my-work',
                    lazy: async () => ({
                      Component: (await import('@/features/my-work/my-work-page')).MyWorkPage,
                    }),
                  },
                  // The content calendar is My work's calendar now (2026-10-15); old links still work.
                  {
                    path: 'content-calendar',
                    element: <Navigate to="../my-work?view=calendar&who=anyone" replace />,
                  },
                  {
                    path: 'news',
                    lazy: async () => ({
                      Component: (await import('@/features/announcements/news-page')).NewsPage,
                    }),
                  },
                  {
                    path: 'news/:announcementId',
                    lazy: async () => ({
                      Component: (await import('@/features/announcements/news-page')).NewsPage,
                    }),
                  },
                  {
                    path: 'notifications',
                    lazy: async () => ({
                      Component: (await import('@/features/notifications/notifications-page'))
                        .NotificationsPage,
                    }),
                  },
                  {
                    path: 'prospects',
                    lazy: async () => ({
                      Component: (await import('@/features/prospects/prospects-page')).ProspectsPage,
                    }),
                  },
                  {
                    path: 'prospects/:prospectId',
                    lazy: async () => ({
                      Component: (await import('@/features/prospects/prospect-page')).ProspectPage,
                    }),
                  },
                  // Support moved to support.arvaseo.com (/tickets); these keep the sidebar link and old links working.
                  {
                    path: 'support',
                    lazy: async () => ({
                      Component: (await import('@/features/support/support-redirect')).SupportRedirect,
                    }),
                  },
                  {
                    path: 'support/:ticketId',
                    lazy: async () => ({
                      Component: (await import('@/features/support/support-redirect')).SupportRedirect,
                    }),
                  },
                  {
                    path: 'integrations',
                    lazy: async () => ({
                      Component: (await import('@/features/google/integrations-page')).IntegrationsPage,
                    }),
                  },
                  {
                    path: 'wordpress',
                    lazy: async () => ({
                      Component: (await import('@/features/wordpress/workspace-health-page'))
                        .WorkspaceHealthPage,
                    }),
                  },
                  {
                    path: 'referrals',
                    lazy: async () => ({
                      Component: (await import('@/features/referrals/referrals-page')).ReferralsPage,
                    }),
                  },
                  {
                    path: 'projects/:projectId',
                    lazy: async () => ({
                      Component: (await import('@/features/projects/project-layout')).ProjectLayout,
                    }),
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (await import('@/features/projects/pages/project-overview-page'))
                            .ProjectOverviewPage,
                        }),
                      },
                      {
                        path: 'integrations',
                        lazy: async () => ({
                          Component: (await import('@/features/projects/pages/project-integrations-page'))
                            .ProjectIntegrationsPage,
                        }),
                      },
                      {
                        path: 'settings',
                        lazy: async () => ({
                          Component: (await import('@/features/projects/pages/project-settings-page'))
                            .ProjectSettingsPage,
                        }),
                      },
                      {
                        path: 'wordpress',
                        lazy: async () => ({
                          Component: (await import('@/features/wordpress/wordpress-page')).WordPressPage,
                        }),
                      },
                      {
                        path: 'audit',
                        lazy: async () => ({
                          Component: (await import('@/features/audit/audit-page')).AuditPage,
                        }),
                      },
                      {
                        path: 'performance',
                        lazy: async () => ({
                          Component: (await import('@/features/performance/performance-page'))
                            .PerformancePage,
                        }),
                      },
                      {
                        path: 'keywords',
                        lazy: async () => ({
                          Component: (await import('@/features/keywords/keywords-page')).KeywordsPage,
                        }),
                      },
                      {
                        path: 'cannibalization',
                        lazy: async () => ({
                          Component: (await import('@/features/cannibalization/cannibalization-page'))
                            .CannibalizationPage,
                        }),
                      },
                      {
                        path: 'mapping',
                        lazy: async () => ({
                          Component: (await import('@/features/mapping/mapping-page')).MappingPage,
                        }),
                      },
                      {
                        path: 'on-page',
                        lazy: async () => ({
                          Component: (await import('@/features/onpage/onpage-page')).OnPagePage,
                        }),
                      },
                      {
                        path: 'on-page/:pageHash',
                        lazy: async () => ({
                          Component: (await import('@/features/onpage/onpage-detail-page')).OnPageDetailPage,
                        }),
                      },
                      {
                        path: 'content',
                        lazy: async () => ({
                          Component: (await import('@/features/content/content-page')).ContentPage,
                        }),
                      },
                      {
                        path: 'content/:pieceId',
                        lazy: async () => ({
                          Component: (await import('@/features/content/content-detail-page'))
                            .ContentDetailPage,
                        }),
                      },
                      {
                        path: 'ai-search',
                        lazy: async () => ({
                          Component: (await import('@/features/ai-search/ai-search-page')).AiSearchPage,
                        }),
                      },
                      {
                        path: 'local',
                        lazy: async () => ({
                          Component: (await import('@/features/local/local-seo-page')).LocalSeoPage,
                        }),
                      },
                      {
                        path: 'off-page',
                        lazy: async () => ({
                          Component: (await import('@/features/offpage/offpage-page')).OffPagePage,
                        }),
                      },
                      {
                        path: 'competitors',
                        lazy: async () => ({
                          Component: (await import('@/features/competitors/competitors-page'))
                            .CompetitorsPage,
                        }),
                      },
                      {
                        path: 'analytics',
                        lazy: async () => ({
                          Component: (await import('@/features/analytics/analytics-page')).AnalyticsPage,
                        }),
                      },
                      {
                        path: 'reports',
                        lazy: async () => ({
                          Component: (await import('@/features/reports/reports-page')).ReportsPage,
                        }),
                      },
                      {
                        path: 'reports/assessments/:assessmentId',
                        lazy: async () => ({
                          Component: (await import('@/features/reports/assessment-delivery'))
                            .SavedAssessmentPage,
                        }),
                      },
                      {
                        path: 'reports/:reportId',
                        lazy: async () => ({
                          Component: (await import('@/features/reports/report-view-page')).ReportViewPage,
                        }),
                      },
                      {
                        path: 'tasks',
                        lazy: async () => ({
                          Component: (await import('@/features/tasks/tasks-page')).TasksPage,
                        }),
                      },
                      {
                        path: 'guide',
                        lazy: async () => ({
                          Component: (await import('@/features/guide/guide-page')).GuidePage,
                        }),
                      },
                      {
                        path: 'activity',
                        lazy: async () => ({
                          Component: (await import('@/features/activity/activity-page')).ActivityPage,
                        }),
                      },
                      {
                        path: 'seo-file',
                        lazy: async () => ({
                          Component: (await import('@/features/seo-file/seo-file-page')).SeoFilePage,
                        }),
                      },
                      {
                        path: 'benchmark',
                        lazy: async () => ({
                          Component: (await import('@/features/benchmark/benchmark-page')).BenchmarkPage,
                        }),
                      },
                      {
                        path: ':module',
                        lazy: async () => ({
                          Component: (await import('@/features/projects/pages/project-module-page'))
                            .ProjectModulePage,
                        }),
                      },
                    ],
                  },
                  // The Billing link once pointed here; billing lives in Settings.
                  { path: 'billing', element: <Navigate to="../settings/plan" replace /> },
                  {
                    path: ':module',
                    lazy: async () => ({
                      Component: (await import('@/features/shell/upcoming-module-page')).UpcomingModulePage,
                    }),
                  },
                ],
              },
            ],
          },
        ],
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
